import FeaturedServicesCarousel from '@/components/featured-services-carousel';
import { HomeMobileActions } from '@/components/home-mobile-actions';
import { ServiceAutocomplete } from '@/components/ServiceAutocomplete';
import { SubNavContainer } from '@/components/sub-nav-container';
import { useAboutUs, useFaqs, useServices } from '@/hooks/useQueries';
import { API_CONFIG } from '@/lib/config';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Badge,
  Button,
  Skeleton,
} from '@opaz/ui-components';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowRight, Loader2Icon } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';

export const Route = createFileRoute('/')({
  component: HomePage,
  head: () => {
    return {
      meta: [
        {
          title: 'Home - OPAZ E-Service Portal',
        },
      ],
    };
  },
});

function HomePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const { data: aboutUsData } = useAboutUs();

  const { data: popularSearchedServices, isLoading: popularSearchedServicesLoading } = useServices({
    filters: {
      isFeatured: { $eq: true },
    },
  });

  const { data: services, isLoading: servicesLoading } = useServices({
    pagination: {
      limit: 100,
    },
  });

  const { data: featuredQuestions, isLoading: featuredQuestionsLoading } = useFaqs({
    filters: {
      isFeatured: { $eq: true },
    },
    pagination: {
      pageSize: 4,
    },
  });

  return (
    <>
      <SubNavContainer>
        <div className="max-w-container-960 pb-6 md:pb-8 w-full">
          <div className="mb-8 md:mb-10">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 text-foreground">
              <Trans i18nKey="home.title" />
            </h1>
            <p className="text-lg sm:text-xl text-foreground">{t('home.subtitle')}</p>
          </div>

          <div className="space-y-5">
            <div className="flex flex-col justify-center md:justify-start md:flex-row md:items-center gap-2">
              <p className="m-0 p-0 whitespace-nowrap">{t('home.popularSearch')}</p>
              {popularSearchedServicesLoading && (
                <div className="flex flex-wrap items-center gap-2">
                  {Array.from({ length: 3 }).map((_, idx) => (
                    <Skeleton aria-hidden="true" key={idx} className="h-10 w-32 rounded-full bg-white " />
                  ))}

                  <div role="status" aria-live="polite" className="sr-only">
                    Loading featured services...
                  </div>
                </div>
              )}
              {!popularSearchedServicesLoading &&
                popularSearchedServices &&
                popularSearchedServices?.data?.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    {popularSearchedServices?.data?.slice(0, 6).map((service) => (
                      <Badge
                        key={service.documentId}
                        className="rounded-full border-none text-link font-normal bg-white hover:bg-gray-100 hover:text-gray-800 cursor-pointer py-2 px-4 min-h-10"
                        onClick={() => {
                          navigate({
                            to: '/services/services-catalogue/$id',
                            params: { id: service.documentId },
                          });
                        }}
                        title={service.title}
                      >
                        {service.title}
                      </Badge>
                    ))}
                  </div>
                )}
            </div>
            <ServiceAutocomplete />
          </div>
        </div>
      </SubNavContainer>

      <HomeMobileActions services={services?.data ?? []} isLoading={servicesLoading} />

      <div className="bg-gray-50 w-full py-12 md:py-20">
        <div className="max-w-container-960 mx-auto px-4 md:px-0 space-y-12">
          <FeaturedServicesCarousel services={services?.data ?? []} isLoading={servicesLoading} />
          <div className="flex items-center justify-center">
            <Button
              variant="secondary"
              className="h-10 rounded-full px-6"
              onClick={() => {
                navigate({
                  to: '/services/services-catalogue',
                  search: {
                    id: undefined,
                  },
                });
              }}
            >
              {t('home.seeAllServices')}
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-white w-full my-12 md:my-20">
        <div className="max-w-container-960 mx-auto px-4 md:px-0">
          <div className="hidden md:block space-y-16">
            <div className="flex items-center justify-between w-full">
              <h2 className="flex flex-col text-3xl md:text-4xl font-normal text-[#1A444F]">
                {i18n.language === 'ar' ? (
                  // --- ARABIC VERSION (Single Line) ---
                  <span>
                    <Trans
                      i18nKey="home.faqTitle" // The single key for Arabic
                      components={{
                        1: <strong className="font-bold" />, // Maps <1> to a bold tag
                      }}
                    />
                  </span>
                ) : (
                  // --- ENGLISH VERSION (Two Lines) ---
                  <>
                    <span>
                      <Trans
                        i18nKey="home.faqTitleLine1" // First line of the English title
                        components={{
                          1: <strong className="font-bold" />, // Maps <1> to a bold tag
                        }}
                      />
                    </span>
                    <span>
                      <Trans
                        i18nKey="home.faqTitleLine2" // Second line of the English title
                        components={{
                          1: <strong className="font-bold" />, // Maps <1> to a bold tag
                        }}
                      />
                    </span>
                  </>
                )}
              </h2>
            </div>

            {featuredQuestionsLoading && (
              <div className="flex items-center justify-center">
                <Loader2Icon className="h-12 w-12 animate-spin text-[#4F9B8F]" />

                <div role="status" aria-live="polite" className="sr-only">
                  Loading featured frequently asked questions...
                </div>
              </div>
            )}

            <Accordion type="multiple" className="w-full" lang={i18n.language}>
              {!featuredQuestionsLoading &&
                featuredQuestions &&
                featuredQuestions?.data?.length > 0 &&
                featuredQuestions?.data?.map((question) => (
                  <AccordionItem key={question.documentId} value={question.documentId}>
                    <AccordionTrigger
                      className="font-bold text-lg"
                      translations={{ show: t('common.show'), hide: t('common.hide') }}
                    >
                      {question.question}
                    </AccordionTrigger>
                    <AccordionContent className="whitespace-pre-wrap">{question.answer}</AccordionContent>
                  </AccordionItem>
                ))}
            </Accordion>

            <div className="flex items-center justify-center">
              <Button
                variant="secondary"
                className="h-10 rounded-full px-6"
                onClick={() => {
                  navigate({ to: '/faq' });
                  window.scrollTo(0, 0);
                }}
              >
                {t('home.seeAllQuestions')}
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-center md:hidden">
            <Button
              variant="secondary"
              className="h-10 rounded-full px-6"
              onClick={() => {
                navigate({ to: '/faq' });
                window.scrollTo(0, 0);
              }}
            >
              {t('faq.title')}
            </Button>
          </div>

          <div className="relative mt-16 rounded-3xl shadow-md overflow-hidden">
            {aboutUsData?.data?.homePageImage?.url && (
              <img
                src={`${API_CONFIG.STRAPI_BASE_URL.replace('/api', '')}${aboutUsData?.data?.homePageImage?.url}`}
                alt="Two people discussing support topics, representing FAQ"
                className="w-full h-full object-cover object-center"
                loading="lazy"
              />
            )}
            <div className="absolute inset-0 bg-[#090E0BB2]"></div>
            <div className="absolute inset-0 flex flex-col gap-4 items-start justify-end text-white p-6 sm:p-10 md:p-16 lg:p-20">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold">{t('home.commentSuggestions')}</h2>
              <p className="text-base sm:text-lg md:text-xl mb-6 max-w-2xl">
                {t('home.commentSuggestionsDescription')}
              </p>
              <Button
                onClick={() => {
                  navigate({ to: '/feedback' });
                }}
                variant="secondary"
                className="h-10 rounded-full px-6 bg-white text-black"
              >
                {t('home.shareFeedback')}
                <ArrowRight className="ml-2 rtl:rotate-180" aria-hidden="true" focusable="false" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
